import { useState } from "react";

import type { I_Export, I_Location, I_RadiusLocation, I_TbSoftLocation } from "../../shared/types.ts";

interface I_ExportBar {
    origin: I_Location | null
    locations: I_RadiusLocation[]
    radiusKm: number
    countries: string[]
    truncated: boolean
}

type T_Schema = "standard" | "tbsoft";

// Das Zielformat stand anfangs nicht fest, deshalb bleibt dieses hier eine flache Liste hinter
// einem kleinen Umschlag. Ein "map" über "locations" reicht, um daraus etwas anderes zu machen.
const buildStandardExport = (origin: I_Location, locations: I_RadiusLocation[], radiusKm: number, countries: string[]): I_Export => {
    return {
        generatedAt: new Date().toISOString(),
        origin: {
            name: origin.name,
            name2: origin.name2,
            zipcode: origin.zipcode,
            countryCode: origin.countryCode,
            lat: origin.lat,
            lon: origin.lon,
            admin1: origin.admin1,
            admin2: origin.admin2
        },
        radiusKm,
        countries,
        count: locations.length,
        locations: locations.map((location) => ({
            name: location.name,
            name2: location.name2,
            zipcode: location.zipcode,
            countryCode: location.countryCode,
            lat: location.lat,
            lon: location.lon,
            admin1: location.admin1,
            admin2: location.admin2,
            distanceKm: location.distanceKm
        }))
    };
}

// Der TBSoft-Import liest die Datei mit "for (const dataset of jsonData)", also eine reine Liste
// ohne Umschlag. Den Tabellennamen zieht er aus dem Dateinamen, deshalb EXP_TBOR_*.json.
// Was die Geodaten nicht hergeben, bleibt null. Eine 0 in OZONE wäre eine erfundene Lieferzone
// und ein leerer String in OGEBKZ ein erfundenes Gebietskennzeichen, beides landet so in der
// Zieltabelle, als wäre es ein Wert.
const buildTbsoftExport = (locations: I_RadiusLocation[], producerNumber: string, factoryNumber: string): I_TbSoftLocation[] => {
    return locations.map((location, index) => ({
        OPRNR: producerNumber || null,
        OWERKNR: factoryNumber || null,
        ONUM: String(index + 1),
        ONUMEXT: null,
        OLKZ: location.countryCode,
        OPLZ: location.zipcode,
        ONAME: location.name,
        ONAME2: location.name2 || null,
        OZONE: null,
        OZONESPED: null,
        OGEBKZ: null,
        OTZONE: location.distanceKm.toFixed(1)
    }));
}

const ExportBar = ({ origin, locations, radiusKm, countries, truncated }: I_ExportBar) => {
    const [schema, setSchema] = useState<T_Schema>("standard");
    const [producerNumber, setProducerNumber] = useState("");
    const [factoryNumber, setFactoryNumber] = useState("");
    const [copied, setCopied] = useState(false);

    if (!origin || locations.length === 0) return null;

    const serialize = (): string => {
        if (schema === "tbsoft") return JSON.stringify(buildTbsoftExport(locations, producerNumber, factoryNumber), null, 2);

        return JSON.stringify(buildStandardExport(origin, locations, radiusKm, countries), null, 2);
    };

    const fileName = schema === "tbsoft"
        ? `EXP_TBOR_${origin.zipcode}_${radiusKm}km.json`
        : `orte-${origin.zipcode}-${radiusKm}km.json`;

    const copy = async () => {
        await navigator.clipboard.writeText(serialize());

        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const download = () => {
        const url = URL.createObjectURL(new Blob([serialize()], { type: "application/json" }));
        const link = document.createElement("a");

        link.href = url;
        link.download = fileName;
        link.click();

        URL.revokeObjectURL(url);
    };

    return (
        <div className="export">
            <span className="export-count">
                <strong>{locations.length.toLocaleString("de-DE")}</strong> Orte im Umkreis von {radiusKm} km
                {truncated && <em> · Grenze erreicht, Umkreis verkleinern</em>}
            </span>

            <div className="export-actions">
                <div className="schema">
                    <button className={schema === "standard" ? "active" : ""} onClick={() => setSchema("standard")}>Standard</button>
                    <button className={schema === "tbsoft" ? "active" : ""} onClick={() => setSchema("tbsoft")}>TBSoft</button>
                </div>

                {schema === "tbsoft" && (
                    <div className="schema-fields">
                        <input
                            type="number"
                            placeholder="Erzeugernr."
                            value={producerNumber}
                            onChange={(event) => setProducerNumber(event.target.value)}
                        />
                        <input
                            type="number"
                            placeholder="Werksnr."
                            value={factoryNumber}
                            onChange={(event) => setFactoryNumber(event.target.value)}
                        />
                    </div>
                )}

                <button onClick={copy}>{copied ? "Kopiert" : "JSON kopieren"}</button>
                <button className="primary" onClick={download} title={fileName}>JSON herunterladen</button>
            </div>
        </div>
    )
}
export default ExportBar;